import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { Container, Header, Grid, Button, Loader, Form, DropdownProps, Statistic, Segment } from "semantic-ui-react";

import AliancaMenu from "../../components/Menu";
import Footer from "../../components/Footer";

import { format } from "date-fns";

import TableApp, { ITableAppRender } from "../../components/TableApp";

import errors from "../../Utils/ExtractErrors";
import AgentHistoryRepository, { IAgentMonth, IAgentParams } from "../../Repositories/AgentHistoryRepository";
import { GetUser } from "../../services/auth";
import numberFormat from "../../Utils/NumberFormat";
import RegionalManagerRepository, { IRegionalManagerListModel } from "../../Repositories/RegionalManagerRepository";
import SectorManagerRepository, { ISectorManagerListModel } from "../../Repositories/SectorManagerRepository";
import CustomLoader from "../../components/CustomLoader";


const AgentHistory: React.FC = () => {

    const date = new Date();
    const [list, setList] = useState<IAgentMonth[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<IAgentParams>({ year: date.getFullYear().toString(), month: (date.getMonth() + 1).toString(), regionalId: "", sectorId: "" });
    const [regionalManagers, setRegionalManagers] = useState<IRegionalManagerListModel[]>([]);
    const [sectorManagers, setSectorManagers] = useState<ISectorManagerListModel[]>([]);

    const totalFaturamento = list.reduce((a, b) => a + b.faturamento, 0);
    const totalPrejuizo = list.reduce((a, b) => a + b.debitos, 0);
    const totalParados = list.reduce((a, b) => a + b.emAberto, 0);
    const totalParadosAcerto = list.reduce((a, b) => a + b.emAbertoAcerto, 0);
    const totalNovos = list.reduce((a, b) => a + b.cadastros, 0);
    const totalConfs = list.reduce((a, b) => a + b.conferencias, 0);

    const { role } = GetUser();

    const optionsMonth = [
        { key: "1", text: "JANEIRO", value: "1" },
        { key: "2", text: "FEVEREIRO", value: "2" },
        { key: "3", text: "MARÇO", value: "3" },
        { key: "4", text: "ABRIL", value: "4" },
        { key: "5", text: "MAIO", value: "5" },
        { key: "6", text: "JUNHO", value: "6" },
        { key: "7", text: "JULHO", value: "7" },
        { key: "8", text: "AGOSTO", value: "8" },
        { key: "9", text: "SETEMBRO", value: "9" },
        { key: "10", text: "OUTUBRO", value: "10" },
        { key: "11", text: "NOVEMBRO", value: "11" },
        { key: "12", text: "DEZEMBRO", value: "12" }
    ];

    const optionsYear = [
        { key: "1", text: "2020", value: "2020" },
        { key: "2", text: "2021", value: "2021" },
        { key: "3", text: "2022", value: "2022" },
        { key: "4", text: "2023", value: "2023" },
        { key: "5", text: "2024", value: "2024" },
        { key: "6", text: "2025", value: "2025" },
        { key: "7", text: "2026", value: "2026" },
    ];

    const load = async () => {

        setLoading(true);
        try {

            const response = await AgentHistoryRepository.AgentMonth({
                year: state.year,
                month: state.month,
                regionalId: state.regionalId,
                sectorId: state.sectorId
            });

            setList(response);
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    };

    useEffect(() => { load() }, []);

    const onDropdownChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    const LoadRegionalManagers = useCallback(async () => {
        const result = await RegionalManagerRepository.List({
            filter: "Status == 1"
        });

        setRegionalManagers(result.list);
    }, []);

    useEffect(() => { LoadRegionalManagers() }, [LoadRegionalManagers]);

    const LoadSectorManagers = useCallback(async (id: string) => {
        const result = await SectorManagerRepository.List({
            filter: `GerenteRegionalId == "${id}" && Status == 1`
        });

        setSectorManagers(result.list);
    }, []);


    const onRegionalManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value, sectorId: "", agentId: "" }));
        LoadSectorManagers(value?.toString() || "");
    }

    const onSectorManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value, agentId: "" }));
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Data",
            content: (x: IAgentMonth) => format(new Date(x.dataCad), "dd/MM/yyyy"),
            textAlign: "center",
            width: 1
        },
        {
            header: "Representante",
            content: (x: IAgentMonth) => x.representante,
            textAlign: "left",
            width: 6
        },
        {
            header: "Cadastros",
            content: (x: IAgentMonth) => x.cadastros,
            textAlign: "center",
            width: 1
        },
        {
            header: "Parados NOVO/ACERTO",
            content: (x: IAgentMonth) => `${x.emAberto}/${x.emAbertoAcerto}`,
            textAlign: "center",
            width: 2
        },
        {
            header: "Faturamento",
            content: (x: IAgentMonth) => numberFormat.Currency(x.faturamento),
            textAlign: "center",
            width: 2
        },
        {
            header: "Debitos",
            content: (x: IAgentMonth) => numberFormat.Currency(x.debitos),
            textAlign: "center",
            width: 2
        },
        {
            header: "Confs",
            content: (x: IAgentMonth) => x.conferencias,
            textAlign: "center",
            width: 2
        },
    ];

    return (
        <>
            <AliancaMenu />
            <Container className="app-margin">
                {loading ? <Segment><CustomLoader /></Segment> :
                    <Grid columns="equal">
                        <Grid.Row>
                            <Grid.Column>
                                <Header size="huge" content={`Produtividade R.P`} icon="chart line" />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Form>
                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="month"
                                                placeholder="Selecione o mês"
                                                label="Mês"
                                                options={optionsMonth}
                                                value={state.month}
                                                onChange={onDropdownChange}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="year"
                                                placeholder="Selecione o ano"
                                                label="Ano"
                                                options={optionsYear}
                                                value={state.year}
                                                onChange={onDropdownChange}
                                            />
                                        </Form.Field>
                                    </Form.Group>
                                    <Form.Group widths="equal">
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="regionalId"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="G. Regional"
                                                onChange={onRegionalManagerChange}
                                                options={regionalManagers.map(regionalManager => { return { key: regionalManager.id, value: regionalManager.id, text: regionalManager.nome } })}
                                                value={state.regionalId}
                                                clearable
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="sectorId"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="G. Setor"
                                                onChange={onSectorManagerChange}
                                                options={sectorManagers.map(sectorManager => { return { key: sectorManager.id, value: sectorManager.id, text: sectorManager.nome } })}
                                                value={state.sectorId}
                                                clearable
                                            />
                                        </Form.Field>
                                    </Form.Group>
                                </Form>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Segment placeholder>
                                    <Grid.Column>
                                        <Statistic.Group size="tiny" widths="5">
                                            <Statistic color="green">
                                                <Statistic.Value>{numberFormat.Currency(totalFaturamento)}</Statistic.Value>
                                                <Statistic.Label content="Faturamento"></Statistic.Label>
                                            </Statistic>
                                            <Statistic color="red">
                                                <Statistic.Value>{numberFormat.Currency(totalPrejuizo)}</Statistic.Value>
                                                <Statistic.Label content="Débitos"></Statistic.Label>
                                            </Statistic>
                                            <Statistic color="purple">
                                                <Statistic.Value>{totalParados}/{totalParadosAcerto}</Statistic.Value>
                                                <Statistic.Label content="Novo/Acerto"></Statistic.Label>
                                            </Statistic>
                                            <Statistic color="teal">
                                                <Statistic.Value>{totalNovos}</Statistic.Value>
                                                <Statistic.Label content="Novos"></Statistic.Label>
                                            </Statistic>
                                            <Statistic color="blue">
                                                <Statistic.Value>{totalConfs}</Statistic.Value>
                                                <Statistic.Label content="Conferências"></Statistic.Label>
                                            </Statistic>
                                        </Statistic.Group>
                                    </Grid.Column>
                                </Segment>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Button
                                    fluid
                                    icon="search"
                                    color="pink"
                                    content="Pesquisar"
                                    onClick={load}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Column>
                            <Grid.Row>
                                <TableApp
                                    render={tableRender}
                                    list={list}
                                    loading={loading}
                                />
                            </Grid.Row>
                        </Grid.Column>
                    </Grid>
                }
            </Container>
            <Footer />
        </>
    );
};

export default AgentHistory;
